import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * A column the agent can drag wider or narrower, and a handle to drag it by.
 *
 * Pointer events rather than mouse events, and window listeners rather than
 * `setPointerCapture`. Both choices are about the same thing — a drag that
 * survives leaving the element it started on:
 *
 *   - Capture is well supported, but a capture lost mid-gesture (a Safari
 *     scroll-bounce, a context menu, a window losing focus) silently ends the
 *     drag with the panel half-resized. Window listeners plus an explicit
 *     `pointercancel` are the same guarantee with a failure mode we choose.
 *   - Pointer events cover trackpad, mouse and touch in one path. Safari has
 *     had them since 13, so there is nothing to fall back to.
 *
 * The two macOS-specific details, both of which look like "dragging is broken"
 * rather than like a styling bug:
 *
 *   - `preventDefault()` on pointerdown, plus `user-select: none` on <body> for
 *     the duration. Without it Safari starts selecting the text under the
 *     cursor the moment the pointer moves, and the drag turns into a highlight.
 *   - `touch-action: none` on the handle (in CSS), or a trackpad drag is taken
 *     by the page's own scroll/back-swipe gesture before this ever sees it.
 */

/** Below this the list stops being readable; above it the thread does. */
const DEFAULT_MIN = 220;
const DEFAULT_MAX = 560;
/** Whatever the agent asks for, the pane beside it keeps at least this much. */
const NEIGHBOUR_MIN = 380;

function read(key, fallback) {
  try {
    const stored = Number(localStorage.getItem(key));
    return Number.isFinite(stored) && stored > 0 ? stored : fallback;
  } catch {
    // Private mode: resizing still works, it just won't be remembered.
    return fallback;
  }
}

/**
 * @param {object} options
 * @param {string} options.key      localStorage key the width is remembered under
 * @param {number} options.initial  width before the agent has ever dragged it
 * @param {boolean} options.enabled false collapses this to a no-op — on a phone
 *   the panel is the whole screen and there is no border to drag
 */
export function useResizableWidth({
  key,
  initial,
  min = DEFAULT_MIN,
  max = DEFAULT_MAX,
  enabled = true,
}) {
  const [width, setWidth] = useState(() => read(key, initial));
  /** Where the pointer went down, and how wide the panel was at that moment. */
  const origin = useRef(null);
  const [dragging, setDragging] = useState(false);

  /**
   * The ceiling is whichever is smaller: the configured maximum, or what leaves
   * the neighbouring pane usable. On a 1280px laptop that second one is the
   * real limit, and without it the inbox can be dragged over the thread.
   */
  const clamp = useCallback(
    (value) => {
      const room = Math.max(min, window.innerWidth - NEIGHBOUR_MIN);
      return Math.round(Math.min(Math.max(value, min), Math.min(max, room)));
    },
    [min, max]
  );

  useEffect(() => {
    try {
      localStorage.setItem(key, String(width));
    } catch {
      /* Nothing to do; the width is still applied for this session. */
    }
  }, [key, width]);

  /* A width chosen on a wide monitor is too wide on a narrow one. Re-clamped on
     resize rather than rewritten, so plugging the monitor back in restores what
     the agent actually chose. */
  useEffect(() => {
    if (!enabled) return undefined;
    const onResize = () => setWidth((current) => clamp(current));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [enabled, clamp]);

  const onPointerDown = (event) => {
    // Primary button only: a right-click on the border is a context menu, not
    // the start of a drag that then never ends.
    if (!enabled || (event.pointerType === 'mouse' && event.button !== 0)) return;

    event.preventDefault();
    origin.current = { x: event.clientX, width };
    setDragging(true);
  };

  useEffect(() => {
    if (!dragging) return undefined;

    const onMove = (event) => {
      const start = origin.current;
      if (!start) return;
      // Absolute delta from where the drag began, never accumulated per event:
      // a dropped or coalesced move then costs nothing, and the panel cannot
      // drift away from the cursor over a long drag.
      setWidth(clamp(start.width + (event.clientX - start.x)));
    };
    const stop = () => {
      origin.current = null;
      setDragging(false);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', stop);
    window.addEventListener('pointercancel', stop);
    // A drag left running because the window went away is a cursor stuck as a
    // resize arrow over a page that no longer responds to it.
    window.addEventListener('blur', stop);

    /* While dragging, the whole document wears the resize cursor and refuses to
       select. Set on <body> rather than the handle because the pointer spends
       the drag everywhere except the handle. */
    document.body.classList.add('is-resizing');

    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
      window.removeEventListener('blur', stop);
      document.body.classList.remove('is-resizing');
    };
  }, [dragging, clamp]);

  const reset = useCallback(() => setWidth(clamp(initial)), [clamp, initial]);

  /**
   * The keyboard half.
   *
   * A separator that can only be dragged is a control an agent on a keyboard
   * cannot reach at all, and the arrow keys are what a screen reader announces
   * for `role="separator"` anyway. Home returns to the default, which is also
   * what double-clicking the border does.
   */
  const onKeyDown = (event) => {
    if (!enabled) return;
    const step = event.shiftKey ? 64 : 16;

    if (event.key === 'ArrowLeft') setWidth((w) => clamp(w - step));
    else if (event.key === 'ArrowRight') setWidth((w) => clamp(w + step));
    else if (event.key === 'Home') reset();
    else return;

    event.preventDefault();
  };

  return {
    width: enabled ? clamp(width) : null,
    dragging,
    reset,
    /** Spread onto the handle element; see `ResizeHandle` below. */
    handleProps: {
      onPointerDown,
      onKeyDown,
      onDoubleClick: reset,
      'aria-valuenow': clamp(width),
      'aria-valuemin': min,
      'aria-valuemax': max,
    },
  };
}

/**
 * The border itself.
 *
 * A sibling of the panel rather than a child of it, so it is not clipped by the
 * panel's `overflow: hidden` and does not sit over the list's own scrollbar —
 * dragging that scrollbar has to keep working. Negative margins in CSS let it
 * straddle the border line without taking a column of its own.
 */
export function ResizeHandle({ label, dragging, ...props }) {
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      tabIndex={0}
      className={`resizer${dragging ? ' is-dragging' : ''}`}
      {...props}
    />
  );
}

export default useResizableWidth;
